import { useEffect, useState, useCallback, useMemo } from 'react';
import { useExternalUserSubjectsSlice } from '@restore/ExternalUserSubjects';
import { ViewSelector } from '@components/ViewSelector/ViewSelector';
import { useNavbarContext } from '@components/Navbar/NavbarContext';
import {
  useExternalUserViewSlice,
  ViewType,
} from '@restore/ExternalUserView/externalUserView.slice';
import { useFeatureFlag } from '@dynamika-shared/services/featureFlags';

interface UseSubjectListDataParams {
  clinicalTrialId: string | undefined;
  onSearch: (value: string) => void;
}

export const useSubjectListData = ({
  clinicalTrialId,
  onSearch,
}: UseSubjectListDataParams) => {
  const [searchValue, setSearchValue] = useState('');
  const { setRightContent } = useNavbarContext();
  const { EXTERNAL_USER_VIEW_SELECTOR_FLAG } = useFeatureFlag();

  // Use Redux for view state management - extract only what we need to minimize rerenders
  const [currentView, setViewAction] = useExternalUserViewSlice((s, a) => [
    s?.currentView ?? ViewType.EXTERNAL,
    a.setView,
  ]);

  const handleViewChange = useCallback((view: ViewType) => {
    if (setViewAction) {
      setViewAction(view);
    }
  }, [setViewAction]);

  const [
    state,
    actions,
    context,
    contextLoading,
    trials,
    trialsLoading,
  ] = useExternalUserSubjectsSlice((s, a) => [
    s,
    a,
    s?.context,
    s?.contextLoading,
    s?.trials,
    s?.trialsLoading,
  ]);

  const selectedSiteFilter = state?.selectedSiteFilter;

  // Memoize sites to prevent creating new array on every render
  const sites = useMemo(() =>
    context?.sites || [],
    [context?.sites]
  );

  // Fetch context and trials on mount
  useEffect(() => {
    if (clinicalTrialId && actions?.fetchContext) {
      actions.fetchContext({ clinicalTrialId });
    }
    if (actions?.fetchTrials) {
      actions.fetchTrials({ role: 'EXTERNAL_USER' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalTrialId]);

  // Set first site as default filter when sites are loaded or clinical trial changes
  useEffect(() => {
    if (sites.length > 0 && actions?.setSiteFilter) {
      actions.setSiteFilter(sites[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites.length, clinicalTrialId]);

  // Reset search when clinicalTrialId or site changes
  useEffect(() => {
    onSearch('');
  }, [clinicalTrialId, selectedSiteFilter, onSearch]);

  // Manage navbar right content (ViewSelector)
  // Controlled by feature flag: externalUserViewSelector
  useEffect(() => {
    if (EXTERNAL_USER_VIEW_SELECTOR_FLAG) {
      setRightContent(
        <ViewSelector selectedView={currentView} onChange={handleViewChange} />
      );
    } else {
      setRightContent(null);
    }

    return () => {
      setRightContent(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, handleViewChange, EXTERNAL_USER_VIEW_SELECTOR_FLAG]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      onSearch(value);
    },
    [onSearch]
  );

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    onSearch('');
  }, [onSearch]);

  const handleTabChange = useCallback(
    (siteId: string) => {
      if (actions?.setSiteFilter) {
        actions.setSiteFilter(siteId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    state,
    actions,
    context,
    contextLoading,
    trials,
    trialsLoading,
    searchValue,
    currentView,
    selectedSiteFilter,
    sites,
    handleSearchChange,
    handleClearSearch,
    handleTabChange,
  };
};
